/** @jsxImportSource @emotion/react */
import { ReactNode, useCallback, useMemo } from "react";
import Select, { GroupBase, Props as SelectProps, StylesConfig } from "react-select";
import CreatableSelect from "react-select/creatable";
import { FormGroup, FormText, Label } from "reactstrap";

export interface OptionType {
  label: string;
  value: string;
  meta?: { key: string; value: string }[]; // extra info shown/searched next to the label
  __missing?: boolean; // custom flag
}

interface SingleSelectProps {
  setValue: (field: string, value: any) => void;
  options: OptionType[];
  post_value?: string | number | string[] | OptionType | OptionType[];
  field: string;
  label?: string | boolean;
  disabled?: boolean;
  formatOptionLabel?: SelectProps<OptionType, boolean, GroupBase<OptionType>>["formatOptionLabel"];
  filterOption?: SelectProps<OptionType, boolean, GroupBase<OptionType>>["filterOption"];
  multi?: boolean;
  return_objects?: boolean;
  return_array?: boolean;
    description?: ReactNode;
  isClearable?: boolean;
  placeholder?: string;
  unwrapped?: boolean;
  isCreatable?: boolean;
  selectComponents?: SelectProps<OptionType, boolean, GroupBase<OptionType>>["components"];
}

export default function SingleSelect({
    setValue,
    options,
    post_value,
    field,
    label,
    disabled = false,
    formatOptionLabel,
    filterOption,
    multi = false,
    return_objects = false,
    return_array = false,
    description = "",
    isClearable = true,
    placeholder = undefined,
    unwrapped = false,
    isCreatable = false,
    selectComponents,
}: SingleSelectProps) {

    const _onChange = (option: any) => {
        if (option) {
            if (return_objects) {
                setValue(field, option);
            } else if (return_array) {
                setValue(field, option.map((i: OptionType) => i.value));
            } else {
                setValue(field, multi ? option.map((i: OptionType) => i.value).join(",") : option.value);
            }
        } else {
            setValue(field, multi ? [] : null);
        }
    };

    const { normalizedOptions, selectedValue } = useMemo(() => {
        const optionsCopy = [...options];
        let selected: OptionType | OptionType[] | null = null;

        const addMissingOption = (value: string): OptionType => ({
            label: `${value} (NA)`,
            value,
            __missing: true,
        });

        if (return_objects && post_value) {
            selected = post_value as OptionType | OptionType[];
        } else if (multi) {
            let selectedValues: string[] = [];

            if (return_array && Array.isArray(post_value)) {
                selectedValues = post_value as string[];
            } else {
                selectedValues = post_value ? String(post_value).split(",") : [];
            }

            selected = selectedValues.map((val) => {
                const existing = options.find((opt) => opt.value === val);
                if (existing) return existing;
                const missing = addMissingOption(val);
                optionsCopy.push(missing);
                return missing;
            });
        } else {
            const val = post_value ? String(post_value) : "";
            const existing = options.find((opt) => opt.value === val);
            if (existing) {
                selected = existing;
            } else if (val) {
                const missing = addMissingOption(val);
                optionsCopy.push(missing);
                selected = missing;
            } else {
                selected = null;
            }
        }

        return {
            normalizedOptions: optionsCopy,
            selectedValue: selected,
        };
    }, [options, post_value, return_objects, return_array, multi]);

    const customFormatOptionLabel = useCallback((data, meta) => {
        if (data.__missing) {
            return <span style={{ opacity: 0.8 }}>{data.label}</span>;
        }
        return formatOptionLabel ? formatOptionLabel(data, meta) : data.label;
    }, [formatOptionLabel]);

    const customStyles = useMemo<StylesConfig<OptionType, boolean>>(() => ({
        singleValue: (base, props) => ({
            ...base,
            opacity: props.data.__missing ? 0.8 : 1,
        }),
        multiValueLabel: (base, props) => ({
            ...base,
            opacity: props.data.__missing ? 0.8 : 1,
        }),
        menuPortal: (base) => ({ ...base, zIndex: 99999 })
    }), []);

    const SelectComponent = isCreatable ? CreatableSelect : Select;

    const select = <SelectComponent
        id={field}
        options={normalizedOptions}
        isMulti={multi}
        isClearable={isClearable}
        formatOptionLabel={customFormatOptionLabel}
        filterOption={filterOption}
        styles={customStyles}
        value={selectedValue}
        onChange={_onChange}
        isDisabled={disabled}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        placeholder={placeholder || undefined}
        components={selectComponents}
    />;

    if (unwrapped) {
        return select;
    }

    return (
        <FormGroup>
            {label && <Label>{label}</Label>}
            {select}
            {description && <FormText className="d-block">{description}</FormText>}
        </FormGroup>
    );
}
